import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";
import { jwt_decode } from "../../utils/jwt_decode";
import {
  Globe,
  Network,
  X,
  Check,
  AlertTriangle,
  Loader2
} from "lucide-react";

interface ExternalAsset {
  id: number;
  client_id: number;
  client_name: string;
  name: string;
  asset_type: string;
  ip_address: string | null;
  domain: string | null;
  fqdn: string | null;
  created: string;
  updated: string;
  resolved_tags?: string;
  tags?: string;
  protocols_performed?: string | string[];
}

interface AddExternalAssetModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientId?: string | number;
  mode?: "add" | "edit";
  asset?: ExternalAsset | null;
}

const tagsToComma = (tags: any): string => {
  try {
    if (!tags) return "";
    const parsed = typeof tags === "string" ? JSON.parse(tags) : tags;
    return Array.isArray(parsed) ? parsed.join(", ") : "";
  } catch {
    return "";
  }
};

export default function AddExternalAssetModal({
  open,
  onClose,
  onSuccess,
  clientId,
  mode = "add",
  asset = null,
}: AddExternalAssetModalProps) {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [tags, setTags] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      if (mode === "edit" && asset) {
        setName(asset.name || "");
        setDomain(asset.domain || "");
        setIpAddress(asset.ip_address || "");
        setTags(tagsToComma(asset.resolved_tags || asset.tags));
      } else {
        setName("");
        setDomain("");
        setIpAddress("");
        setTags("");
      }
      setErrors({});
    }
  }, [open, mode, asset]);

  if (!open) return null;

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!name.trim()) {
      nextErrors.name = "Name is required";
    }
    if (!domain.trim() && !ipAddress.trim()) {
      nextErrors.value = "Either Domain or IP Address is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (!clientId) {
      showError("Client ID is required");
      return;
    }

    setLoading(true);
    try {
      const decodedToken = jwt_decode();
      const organizationId = decodedToken?.organization_id;

      const payload = {
        client_id: Number(clientId),
        organization_id: organizationId ? Number(organizationId) : null,
        name: name.trim(),
        asset_type: "external_asset",
        domain: domain.trim() || null,
        ip_address: ipAddress.trim() || null,
        tags: tags
          ? JSON.stringify(
            tags
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean)
          )
          : "[]",
        status: "active",
      };

      const response =
        mode === "edit" && asset?.id
          ? await baseapi.put(`/assets/${asset.id}`, payload)
          : await baseapi.post("/assets", payload);

      if (response.data && response.data.status) {
        showSuccess(
          mode === "edit"
            ? "Asset updated successfully"
            : "Asset added successfully"
        );
        onSuccess();
        onClose();
      } else {
        showError(response.data.message || "Failed to save asset");
      }
    } catch (error: any) {
      showError(error.message || "An error occurred while saving asset");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
        <div className="w-full max-w-lg bg-white dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200 dark:border-orange-400/20 shadow-2xl rounded-xl text-gray-900 dark:text-white relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />

          <div className="sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800 px-6 py-4 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-orange-500 dark:text-orange-400" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {mode === "edit" ? "Edit External Asset" : "Add External Asset"}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              {/* Name field */}
              <div>
                <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                  <Globe className="w-3 h-3 mr-1 text-blue-500 dark:text-blue-400" />
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full bg-gray-50 dark:bg-gray-800/50 border ${errors.name ? "border-red-500 dark:border-red-400" : "border-gray-300 dark:border-gray-700"
                    } rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 dark:focus:border-orange-400 focus:ring-1 focus:ring-orange-500 dark:focus:ring-orange-400`}
                  placeholder="External Server 1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400 flex items-center">
                    <AlertTriangle className="w-3 h-3 mr-1" /> {errors.name}
                  </p>
                )}
              </div>

              {/* Domain field */}
              <div>
                <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                  <Network className="w-3 h-3 mr-1 text-purple-500 dark:text-purple-400" />
                  Domain
                </label>
                <input
                  type="text"
                  className={`w-full bg-gray-50 dark:bg-gray-800  border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 dark:focus:border-orange-400 focus:ring-1 focus:ring-orange-500 dark:focus:ring-orange-400 disabled:opacity-40 disabled:bg-gray-100 dark:disabled:bg-gray-900/50 disabled:cursor-not-allowed`}
                  placeholder="example.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  disabled={!!ipAddress}
                />
              </div>

              {/* IP Address field */}
              <div>
                <label className="flex items-center text-xs font-medium text-gray-700 dark:text-white mb-1">
                  <Network className="w-3 h-3 mr-1 text-cyan-600 dark:text-cyan-400" />
                  IP Address
                </label>
                <input
                  type="text"
                  className={`w-full bg-gray-50 dark:bg-gray-800  border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 dark:focus:border-orange-400 focus:ring-1 focus:ring-orange-500 dark:focus:ring-orange-400 disabled:opacity-40 disabled:bg-gray-100 dark:disabled:bg-gray-900/50 disabled:cursor-not-allowed`}
                  placeholder="192.168.1.100"
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  disabled={!!domain}
                />
              </div>

              {errors.value && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400 flex items-center">
                  <AlertTriangle className="w-3 h-3 mr-1" /> {errors.value}
                </p>
              )}

              {/* Tags field */}
              {/* <div>
                <label className="flex items-center text-xs font-medium text-white mb-1">
                  <Package className="w-3 h-3 mr-1 text-rose-400" />
                  Tags
                </label>
                <input
                  type="text"
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                  placeholder="prod, external, custom"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                />
                <p className="text-[10px] text-gray-500 mt-0.5">
                  Separate tags with commas
                </p>
              </div> */}
            </div>

            {/* Footer actions */}
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 disabled:opacity-50 transition-colors"
              >
                <X className="w-3 h-3" />
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-1 px-4 py-1.5 text-sm bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg font-semibold hover:from-orange-700 hover:to-orange-800 disabled:opacity-50 min-w-[120px] justify-center shadow-sm"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-3 h-3" />
                    {mode === "edit" ? "Update" : "Add"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
}
